import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type {
  Inserts,
  SocialAdPlacement,
  SocialAssetKind,
  SocialPlatform,
  SocialPostStatus,
  SocialPostType,
  Tables,
} from '@/types/database';

/**
 * Hooks for the Social Content Manager (migration 81, SOCIAL.md).
 *
 * Access model (DB-enforced via RLS): super_admin / admin / manager can
 * read and write every planning table; metric snapshot tables are
 * read-only from the client. See 81_social_content.sql.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SocialPost = Tables<'social_posts'>;
export type SocialPostTarget = Tables<'social_post_targets'>;
export type SocialAsset = Tables<'social_assets'>;
export type SocialPostComment = Tables<'social_post_comments'>;
export type MarketingCampaign = Tables<'marketing_campaigns'>;

export type SocialProfileLite = Pick<
  Tables<'profiles'>,
  'id' | 'full_name' | 'email' | 'avatar_url'
>;

/** Post row with everything the planner needs flattened for rendering. */
export type SocialPostWithDetails = SocialPost & {
  assignee: SocialProfileLite | null;
  campaign: Pick<MarketingCampaign, 'id' | 'name' | 'status'> | null;
  targets: SocialPostTarget[];
};

export type SocialCommentWithAuthor = SocialPostComment & {
  author: SocialProfileLite | null;
};

// ---------------------------------------------------------------------------
// Query-key helpers
// ---------------------------------------------------------------------------

export const socialKeys = {
  all: ['social'] as const,
  posts: () => [...socialKeys.all, 'posts'] as const,
  comments: (postId: string) => [...socialKeys.all, postId, 'comments'] as const,
  assets: (postId: string) => [...socialKeys.all, postId, 'assets'] as const,
  assetUrl: (path: string) => [...socialKeys.all, 'asset-url', path] as const,
  campaigns: () => [...socialKeys.all, 'campaigns'] as const,
};

// Storage bucket for post media + working files (see migration 81). Path
// shape is `<organization_id>/<post_id>/<timestamp>-<filename>`.
const SOCIAL_ASSETS_BUCKET = 'social-assets';

const POST_SELECT =
  'id, organization_id, created_by, assignee_id, title, master_copy, post_type, status, ' +
  'due_date, scheduled_date, scheduled_time, timezone, campaign_id, ad_placement, ' +
  'ad_budget_cents, ad_call_to_action, ad_destination_url, created_at, updated_at, ' +
  'assignee:profiles!social_posts_assignee_id_fkey(id, full_name, email, avatar_url), ' +
  'campaign:marketing_campaigns(id, name, status), ' +
  'targets:social_post_targets(id, organization_id, post_id, platform, account_id, copy_override, status, scheduled_at, published_at, external_post_id, external_url, external_ad_id, error_message, created_at, updated_at)';

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------

/**
 * Every post in the org — the planner is one shared surface, so a single
 * cached list feeds the calendar grid, the unscheduled tray and the list
 * view (Phase 1 volume makes a range filter unnecessary).
 */
export function useSocialPosts() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: socialKeys.posts(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_posts')
        .select(POST_SELECT)
        .order('scheduled_date', { ascending: true, nullsFirst: false })
        .order('scheduled_time', { ascending: true, nullsFirst: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SocialPostWithDetails[];
    },
  });
}

export interface SocialPostInput {
  title: string;
  master_copy: string;
  post_type: SocialPostType;
  status: SocialPostStatus;
  assignee_id: string | null;
  due_date: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  timezone: string;
  campaign_id: string | null;
  ad_placement: SocialAdPlacement | null;
  ad_budget_cents: number | null;
  ad_call_to_action: string | null;
  ad_destination_url: string | null;
  /** Which platforms this post fans out to (one target row each). */
  platforms: SocialPlatform[];
  /** Optional per-platform copy; absent / empty = use master_copy. */
  copyOverrides: Partial<Record<SocialPlatform, string>>;
}

function targetRows(
  input: SocialPostInput,
  postId: string,
  organizationId: string,
): Inserts<'social_post_targets'>[] {
  return input.platforms.map((platform) => ({
    organization_id: organizationId,
    post_id: postId,
    platform,
    status: input.status,
    copy_override: input.copyOverrides[platform]?.trim() || null,
  }));
}

export function useCreateSocialPost() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: SocialPostInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { platforms, copyOverrides, ...fields } = input;
      void platforms;
      void copyOverrides;
      const payload: Inserts<'social_posts'> = {
        ...fields,
        organization_id: profile.organization_id,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('social_posts')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      const postId = (data as { id: string }).id;

      if (input.platforms.length > 0) {
        const { error: tErr } = await supabase
          .from('social_post_targets')
          .insert(targetRows(input, postId, profile.organization_id));
        if (tErr) throw tErr;
      }
      return postId;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.posts() });
    },
  });
}

export function useUpdateSocialPost() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (
      input: { id: string; existingTargets: SocialPostTarget[] } & SocialPostInput,
    ) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { id, existingTargets, platforms, copyOverrides, ...fields } = input;

      const { error } = await supabase
        .from('social_posts')
        .update(fields)
        .eq('id', id);
      if (error) throw error;

      // Reconcile targets against the chosen platform set. Removed
      // platforms are deleted; new ones inserted; survivors get their
      // copy override refreshed. Post-level status is mirrored onto
      // every target that hasn't individually finished its lifecycle
      // (published / failed targets keep their own state).
      const existingByPlatform = new Map(
        existingTargets.map((t) => [t.platform, t]),
      );
      const wanted = new Set(platforms);

      const toDelete = existingTargets.filter((t) => !wanted.has(t.platform));
      if (toDelete.length > 0) {
        const { error: dErr } = await supabase
          .from('social_post_targets')
          .delete()
          .in('id', toDelete.map((t) => t.id));
        if (dErr) throw dErr;
      }

      const toInsert = platforms.filter((p) => !existingByPlatform.has(p));
      if (toInsert.length > 0) {
        const rows = targetRows(
          { ...input, platforms: toInsert },
          id,
          profile.organization_id,
        );
        const { error: iErr } = await supabase
          .from('social_post_targets')
          .insert(rows);
        if (iErr) throw iErr;
      }

      for (const platform of platforms) {
        const existing = existingByPlatform.get(platform);
        if (!existing) continue;
        const copy = copyOverrides[platform]?.trim() || null;
        const keepOwnStatus =
          existing.status === 'published' || existing.status === 'failed';
        const nextStatus = keepOwnStatus ? existing.status : fields.status;
        if (existing.copy_override === copy && existing.status === nextStatus) {
          continue;
        }
        const { error: uErr } = await supabase
          .from('social_post_targets')
          .update({ copy_override: copy, status: nextStatus })
          .eq('id', existing.id);
        if (uErr) throw uErr;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.posts() });
    },
  });
}

export function useDeleteSocialPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Grab asset paths first so we can clean up Storage after the
      // cascade wipes the metadata rows.
      const { data: assets } = await supabase
        .from('social_assets')
        .select('storage_path')
        .eq('post_id', id);

      const { error } = await supabase
        .from('social_posts')
        .delete()
        .eq('id', id);
      if (error) throw error;

      const paths = (assets ?? []).map((a) => a.storage_path);
      if (paths.length > 0) {
        // Best-effort: a stale object is less bad than a failed delete.
        await supabase.storage.from(SOCIAL_ASSETS_BUCKET).remove(paths);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.posts() });
    },
  });
}

/**
 * Calendar drag-and-drop: move a post to a new date (or back to the
 * unscheduled tray with `null`). Time / timezone are untouched.
 */
export function useRescheduleSocialPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; scheduled_date: string | null }) => {
      const { error } = await supabase
        .from('social_posts')
        .update({ scheduled_date: input.scheduled_date })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.posts() });
    },
  });
}

/**
 * Phase 1 manual publish flow: the team posts by hand, then pastes the
 * live URL (and, for ads, the ad id from Ads Manager) back into the
 * target. If every sibling target is now published, the post follows.
 */
export function useMarkTargetPublished() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      target: SocialPostTarget;
      external_url: string | null;
      external_post_id: string | null;
      external_ad_id: string | null;
    }) => {
      const { error } = await supabase
        .from('social_post_targets')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          external_url: input.external_url,
          external_post_id: input.external_post_id,
          external_ad_id: input.external_ad_id,
          error_message: null,
        })
        .eq('id', input.target.id);
      if (error) throw error;

      const { data: siblings, error: sErr } = await supabase
        .from('social_post_targets')
        .select('id, status')
        .eq('post_id', input.target.post_id);
      if (sErr) throw sErr;
      const allPublished = (siblings ?? []).every((s) => s.status === 'published');
      if (allPublished) {
        const { error: pErr } = await supabase
          .from('social_posts')
          .update({ status: 'published' })
          .eq('id', input.target.post_id);
        if (pErr) throw pErr;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.posts() });
    },
  });
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

const COMMENT_SELECT =
  'id, organization_id, post_id, author_id, body, created_at, ' +
  'author:profiles!social_post_comments_author_id_fkey(id, full_name, email, avatar_url)';

export function useSocialPostComments(postId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: postId
      ? socialKeys.comments(postId)
      : ['social', 'comments', 'noop'],
    enabled: !!profile && !!postId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_post_comments')
        .select(COMMENT_SELECT)
        .eq('post_id', postId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SocialCommentWithAuthor[];
    },
  });
}

export function useAddSocialPostComment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { postId: string; body: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'social_post_comments'> = {
        organization_id: profile.organization_id,
        post_id: input.postId,
        author_id: profile.id,
        body: input.body.trim(),
      };
      const { error } = await supabase
        .from('social_post_comments')
        .insert(payload);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: socialKeys.comments(vars.postId) });
    },
  });
}

export function useDeleteSocialPostComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; postId: string }) => {
      const { error } = await supabase
        .from('social_post_comments')
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: socialKeys.comments(vars.postId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Assets — upload to Storage then insert the metadata row; storage
// cleanup on delete is best-effort. Mirrors the calendar-attachments
// pattern.
// ---------------------------------------------------------------------------

const ASSET_SELECT =
  'id, organization_id, post_id, uploaded_by, storage_path, file_name, mime_type, size_bytes, kind, is_publishable, sort_order, alt_text, created_at';

export function useSocialAssets(postId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: postId ? socialKeys.assets(postId) : ['social', 'assets', 'noop'],
    enabled: !!profile && !!postId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_assets')
        .select(ASSET_SELECT)
        .eq('post_id', postId!)
        .order('is_publishable', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SocialAsset[];
    },
  });
}

function assetKindFromMime(mime: string): SocialAssetKind {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  return 'file';
}

function buildSocialAssetPath(args: {
  organization_id: string;
  post_id: string;
  filename: string;
}): string {
  const stamp = Date.now();
  const cleaned = args.filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${args.organization_id}/${args.post_id}/${stamp}-${cleaned}`;
}

export function useUploadSocialAsset() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      file: File;
      isPublishable: boolean;
      sortOrder?: number;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildSocialAssetPath({
        organization_id: profile.organization_id,
        post_id: input.postId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(SOCIAL_ASSETS_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const payload: Inserts<'social_assets'> = {
        organization_id: profile.organization_id,
        post_id: input.postId,
        uploaded_by: profile.id,
        storage_path: path,
        file_name: input.file.name,
        mime_type: input.file.type || null,
        size_bytes: input.file.size,
        kind: assetKindFromMime(input.file.type || ''),
        is_publishable: input.isPublishable,
        sort_order: input.sortOrder ?? 0,
      };
      const { data, error: dbErr } = await supabase
        .from('social_assets')
        .insert(payload)
        .select(ASSET_SELECT)
        .single();
      if (dbErr) {
        // Roll back the storage object so we don't orphan bytes.
        await supabase.storage.from(SOCIAL_ASSETS_BUCKET).remove([path]);
        throw dbErr;
      }
      return data as SocialAsset;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: socialKeys.assets(vars.postId) });
    },
  });
}

export function useDeleteSocialAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      postId: string;
      storagePath: string;
    }) => {
      const { error: dbErr } = await supabase
        .from('social_assets')
        .delete()
        .eq('id', input.id);
      if (dbErr) throw dbErr;
      await supabase.storage
        .from(SOCIAL_ASSETS_BUCKET)
        .remove([input.storagePath]);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: socialKeys.assets(vars.postId) });
    },
  });
}

/**
 * One-off signed URL fetch for click-to-open downloads (runs in the
 * click handler's task so popup blockers tolerate the await).
 */
export async function getSocialAssetSignedUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(SOCIAL_ASSETS_BUCKET)
    .createSignedUrl(path, 60 * 5);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error('Could not create download link.');
  return data.signedUrl;
}

/** Short-lived signed URL for inline image / video previews. */
export function useSocialAssetSignedUrl(path: string | undefined) {
  return useQuery({
    queryKey: path ? socialKeys.assetUrl(path) : ['social', 'asset-url', 'noop'],
    enabled: !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(SOCIAL_ASSETS_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Campaigns (reuses marketing_campaigns from migration 12)
// ---------------------------------------------------------------------------

export function useMarketingCampaigns() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: socialKeys.campaigns(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('marketing_campaigns')
        .select(
          'id, organization_id, name, channel, starts_on, ends_on, budget_cents, status, metrics, notes, created_at, updated_at',
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MarketingCampaign[];
    },
  });
}

/** Inline "new campaign" creation from the post editor's ad block. */
export function useCreateMarketingCampaign() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'marketing_campaigns'> = {
        organization_id: profile.organization_id,
        name: input.name.trim(),
        channel: 'paid_social',
      };
      const { data, error } = await supabase
        .from('marketing_campaigns')
        .insert(payload)
        .select('id, organization_id, name, channel, starts_on, ends_on, budget_cents, status, metrics, notes, created_at, updated_at')
        .single();
      if (error) throw error;
      return data as MarketingCampaign;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: socialKeys.campaigns() });
    },
  });
}
